/**
 * Money as the admin shows and takes it. Amounts are integer cents everywhere (`Cents` in the
 * contract); a price field shows and takes euros the Dutch way: `4,99` is 499 cents. The parsing
 * works on the digits as text, never through a float, so `0,29` is exactly 29.
 */

const euro = new Intl.NumberFormat("nl-NL", { style: "currency", currency: "EUR" });

/** `€ 4,99` */
export const formatCents = (cents: number): string => euro.format(cents / 100);

/** `4,99`: what a price field shows for 499 cents. */
export const centsToInput = (cents: number): string => {
	const sign = cents < 0 ? "-" : "";
	const absolute = Math.abs(cents);
	return `${sign}${Math.floor(absolute / 100)},${String(absolute % 100).padStart(2, "0")}`;
};

/**
 * What someone typed in a price field, in cents, or `undefined` when it is not an amount: `4,99`,
 * `4`, `4,9`, `€ 4,99`, `1.234,50` and `4.99` all work; negative amounts and more than two decimals
 * do not.
 */
export const parseMoney = (text: string): number | undefined => {
	let normalized = text.replace(/[\s€]/g, "");
	if (normalized.includes(",")) {
		// Dutch: dots group thousands, the comma separates the cents.
		normalized = normalized.replace(/\.(?=\d{3}(?:\D|$))/g, "").replace(",", ".");
	} else if (!/^\d+\.\d{1,2}$/.test(normalized)) {
		// Without a comma a dot is a decimal point only when one or two digits follow it.
		normalized = normalized.replace(/\.(?=\d{3}(?:\D|$))/g, "");
	}
	const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(normalized);
	if (!match) return undefined;
	const euros = Number(match[1]);
	const centsPart = Number((match[2] ?? "").padEnd(2, "0"));
	const cents = euros * 100 + centsPart;
	return Number.isSafeInteger(cents) ? cents : undefined;
};
